import { useCallback, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { AgentDot } from '../../AgentDot';
import { Badge, Button, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { MAESTRO_PILE_ID, PILE_SIZE, pileLayout } from '../../../lib/agent-pile';
import { agentControlProps } from '../../../lib/agent-deselect';
import type { AgentSummary } from '../../../lib/my-agents';
import { MaestroMark } from '../MaestroMark';
import styles from './AgentPile.module.css';

// The stack of agent cards beside the genome (Figma `10261:86167`). The cards overlap
// so the ones behind show only their header, and the FRONT card — the last one, fully
// open — is the selected agent. The Maestro rests at the front; picking an agent on
// the genome slides its card down to take that place, and a card behind can be
// brought forward by pressing it.
//
// A bigger roster tightens the overlap — `--pile-behind` (the number of cards behind the
// open one) drives that compression in the stylesheet — until the strips reach their floor,
// past which the column just grows. The whole team is shown; there is no cutoff. Ordering and
// the resting/focus rule (Maestro at the front, a focus always at the front) are all
// `lib/agent-pile`'s job.

const PAGE = 'your_agents';

interface Props {
  agents: AgentSummary[];
  maestroName: string;
  /** The Maestro's address form ("Maestro"), so its CTA reads "View Maestro" not "View The Maestro". */
  maestroShortName: string;
  maestroRole: string;
  maestroDescription: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function AgentPile({
  agents,
  maestroName,
  maestroShortName,
  maestroRole,
  maestroDescription,
  selectedId,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const ids = useMemo(() => agents.map((a) => a.id || a.name), [agents]);

  // Past PILE_SIZE agents the pile collapses to a readable few and offers a "View all" toggle
  // (Figma `10261:85948`, expanded variant). A selection deeper than the collapsed range forces
  // the pile open so the chosen card is never hidden behind the toggle.
  const [expanded, setExpanded] = useState(false);
  const canToggle = agents.length > PILE_SIZE;
  const focusHidden = selectedId ? ids.indexOf(selectedId) >= PILE_SIZE : false;
  const showAll = !canToggle || expanded || focusHidden;
  const limit = showAll ? undefined : PILE_SIZE;

  const { visible } = useMemo(() => pileLayout(ids, selectedId, limit), [ids, selectedId, limit]);
  const byId = useMemo(() => new Map(agents.map((a) => [a.id || a.name, a])), [agents]);

  const cardEls = useRef(new Map<string, HTMLElement>());
  const registerCard = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) cardEls.current.set(id, el);
      else cardEls.current.delete(id);
    },
    [],
  );

  // FLIP the raised card into the open slot (same as the home OpportunityPile): the reorder is
  // instant, so slide the card coming FORWARD — down the stack — from where it was to where it
  // landed. Receding cards aren't animated: sliding one up through the open slot reads as the page
  // jumping; they just take their new peek. Skipped under prefers-reduced-motion.
  const prevTops = useRef(new Map<string, number>());
  const orderKey = visible.join(',');
  useLayoutEffect(() => {
    const prev = prevTops.current;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const next = new Map<string, number>();
    cardEls.current.forEach((el, id) => {
      const top = el.getBoundingClientRect().top;
      next.set(id, top);
      const was = prev.get(id);
      // was − top < 0 means the card moved DOWN (came forward) — animate only that one.
      if (was !== undefined && !reduce && was - top < 0) {
        el.animate([{ transform: `translateY(${was - top}px)` }, { transform: 'translateY(0px)' }], {
          duration: 420,
          easing: 'cubic-bezier(0.2, 0.8, 0.25, 1)',
        });
      }
    });
    prevTops.current = next;
  }, [orderKey]);

  return (
    <div className={styles.pile}>
      <div className={styles.stack} style={{ '--pile-behind': visible.length - 1 } as CSSProperties}>
        {visible.map((id, i) => {
          const agent = byId.get(id);
          const maestro = !agent;
          return (
            <AgentCard
              key={id}
              id={id}
              registerRef={registerCard(id)}
              front={i === visible.length - 1}
              maestro={maestro}
              name={agent?.name ?? maestroName}
              ctaName={agent?.name ?? maestroShortName}
              role={agent?.title ?? maestroRole}
              description={agent?.description ?? maestroDescription}
              href={maestro ? '/agents/personality' : `/agents/${encodeURIComponent(id)}`}
              onSelect={onSelect}
            />
          );
        })}
      </div>

      {/* A big roster collapses to PILE_SIZE cards and reveals the rest in place — the pile
          IS the roster here (unlike the genome-only frames), so "View all" expands the stack
          rather than sending you to /agents. */}
      {canToggle && (
        <div className={styles.toggleWrap}>
          <Button
            variant="text"
            size="small"
            endIcon={showAll ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
            onClick={() => {
              const next = !showAll;
              setExpanded(next);
              logPageClickBi({ page_name: PAGE, button_name: next ? 'view_all_agents' : 'view_less_agents' });
            }}
          >
            {showAll ? t('agents.pileViewLess') : t('agents.pileViewAll')}
          </Button>
        </div>
      )}
    </div>
  );
}

function AgentCard({
  id,
  name,
  ctaName,
  role,
  description,
  href,
  front,
  maestro,
  registerRef,
  onSelect,
}: {
  id: string;
  name: string;
  ctaName: string;
  role?: string;
  description?: string;
  href: string;
  front: boolean;
  maestro: boolean;
  registerRef: (el: HTMLElement | null) => void;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <article ref={registerRef} className={styles.card} data-front={front || undefined} {...agentControlProps}>
      <header className={styles.header}>
        <span className={styles.avatar} aria-hidden>
          {maestro ? <MaestroMark className={styles.maestroMark} /> : <AgentDot id={id} size={24} />}
        </span>
        <Typography variant="h3" color="strong" component="h3" noWrap className={styles.name}>
          {name}
        </Typography>
        {role && <Badge variant="agent-profession" label={role} className={styles.role} />}
      </header>

      {/* ONLY the open card carries a blurb. The card one back used to start its own, clipped by the card
          in front, so the stack read as cards rather than a list of headers — but a half-sentence sitting
          under a name it does not belong to reads as a rendering fault rather than as depth. The stack's
          depth is carried by the cards themselves. The ones behind are clipped anyway, so their blurb and
          CTA would be focusable but invisible. */}
      {front ? (
        <>
          {description && <p className={styles.description}>{description}</p>}
          <Button
            variant="outlined"
            size="large"
            fullWidth
            href={href}
            className={styles.cta}
            onClick={() =>
              logPageClickBi(
                { page_name: PAGE, button_name: maestro ? 'view_maestro' : 'view_agent', answer: id },
                maestro ? undefined : { entity: { id, type: 'agent' } },
              )
            }
          >
            {t('agents.viewAgent', { name: ctaName })}
          </Button>
        </>
      ) : (
        <button
          type="button"
          className={styles.raise}
          aria-label={t('agents.pileBringForward', { name })}
          onClick={() => {
            onSelect(id);
            logPageClickBi({ page_name: PAGE, button_name: 'raise_agent_card', answer: id });
          }}
        />
      )}
    </article>
  );
}
